import { unstable_serialize } from "swr";
import type { ApiMutationMethod } from "../resources/types";
import { generateRoute } from "../routing/resolveRoute";
import { createApiCacheResource, createApiKey, isApiGetKey } from "./apiKey";
import {
  matchesInvalidatedResourceCache,
  matchesExactApiGetRoute,
  matchesResourceCollectionCache,
  matchesResourceInvalidation,
  matchesBatchApiGetCache,
} from "./matchers";
import { replaceEntityInCollectionCache } from "./replaceEntity";
import {
  revalidateServerPagedCaches,
  type ServerPagedCacheRegistration,
} from "./serverPagedCacheRegistry";
import type {
  ApiMutationCacheContext,
  ApiWriteMethod,
  ResourceCollectionCacheContext,
} from "./types";

const replaceableCollectionCacheNamespaces = ["get", "pagedGet"] as const;

type ResourceCollectionCacheNamespace =
  (typeof replaceableCollectionCacheNamespaces)[number];

const invalidationMethodByWriteMethod: Readonly<
  Record<ApiWriteMethod, ApiMutationMethod>
> = {
  POST: "post",
  PUT: "put",
  DELETE: "delete",
};

function hasResourceCollectionCache<TResponse>(
  context: ApiMutationCacheContext<TResponse>,
): context is ResourceCollectionCacheContext<TResponse> {
  return context.collectionRoute != null;
}

function createResourceCollectionCacheMatcher<TResponse>(
  context: ResourceCollectionCacheContext<TResponse>,
) {
  const { descriptor, collectionRoute, clientType } = context;
  const requestedCollectionRoute = generateRoute(collectionRoute, clientType);

  return (namespace: ResourceCollectionCacheNamespace) => (key: unknown) =>
    matchesResourceCollectionCache(key, {
      descriptor,
      collectionRoute,
      requestedCollectionRoute,
      namespace,
      clientType,
    });
}

function matchesRegisteredResourceCollection<TResponse>(
  registration: ServerPagedCacheRegistration,
  context: ResourceCollectionCacheContext<TResponse>,
) {
  return (
    registration.cacheTag === context.descriptor.cacheTag &&
    registration.clientType === context.clientType &&
    registration.collectionRoute === context.collectionRoute
  );
}

async function revalidateResourceCollections<TResponse>(
  context: ResourceCollectionCacheContext<TResponse>,
) {
  const matchesCollectionCache = createResourceCollectionCacheMatcher(context);

  await Promise.all([
    ...replaceableCollectionCacheNamespaces.map((namespace) =>
      context.swr.mutate(matchesCollectionCache(namespace)),
    ),
    revalidateServerPagedCaches((registration) =>
      matchesRegisteredResourceCollection(registration, context),
    ),
  ]);
}

async function revalidateUpdatedResource<TResponse>(
  context: ApiMutationCacheContext<TResponse>,
) {
  await context.swr.mutate((key) =>
    matchesExactApiGetRoute(key, context.requestedUrl),
  );

  if (hasResourceCollectionCache(context)) {
    await revalidateResourceCollections(context);
  }
}

async function revalidateInvalidatedCaches<TResponse>(
  method: ApiWriteMethod,
  context: ApiMutationCacheContext<TResponse>,
) {
  const invalidations =
    context.descriptor.invalidates?.[invalidationMethodByWriteMethod[method]];
  if (!invalidations?.length) return;

  const matchesInvalidatedCache = (key: unknown) =>
    isApiGetKey(key) &&
    typeof key.route === "string" &&
    matchesInvalidatedResourceCache(key, invalidations, context.clientType);

  await Promise.all([
    context.swr.mutate(matchesInvalidatedCache),
    revalidateServerPagedCaches((registration) =>
      matchesResourceInvalidation(
        registration.cacheTag,
        registration.clientType,
        invalidations,
        context.clientType,
      ),
    ),
  ]);
}

async function evictDeletedResource<TResponse>(
  context: ApiMutationCacheContext<TResponse>,
) {
  const { route, descriptor, collectionRoute, clientType, swr } = context;
  const getKey = createApiKey(route, "get", clientType, {
    cacheResource: createApiCacheResource(descriptor, collectionRoute),
  });

  if (getKey) swr.cache.delete(unstable_serialize(getKey));
  if (!hasResourceCollectionCache(context)) return;

  await revalidateResourceCollections(context);
}

async function revalidateCreatedResource<TResponse>(
  context: ApiMutationCacheContext<TResponse>,
) {
  if (!hasResourceCollectionCache(context)) return;
  await revalidateResourceCollections(context);
}

async function replaceUpdatedResource<TResponse>(
  context: ApiMutationCacheContext<TResponse>,
) {
  const { data, requestedUrl, swr } = context;
  if (data == null || context.descriptor.putCacheStrategy === "revalidate") {
    await revalidateUpdatedResource(context);
    return;
  }

  await swr.mutate((key) => matchesExactApiGetRoute(key, requestedUrl), data, {
    revalidate: false,
  });

  if (!hasResourceCollectionCache(context)) return;

  const { descriptor } = context;
  const matchesCollectionCache = createResourceCollectionCacheMatcher(context);
  const replaceCachedEntity = (cached: unknown) =>
    replaceEntityInCollectionCache(cached, data, descriptor);

  await Promise.all([
    swr.mutate(matchesCollectionCache("get"), replaceCachedEntity, {
      revalidate: false,
    }),
    swr.mutate(matchesCollectionCache("pagedGet"), replaceCachedEntity, {
      revalidate: false,
    }),
    revalidateServerPagedCaches((registration) =>
      matchesRegisteredResourceCollection(registration, context),
    ),
  ]);
}

export async function updateGetCachesAfterMutation<TResponse>(
  method: ApiWriteMethod,
  context: ApiMutationCacheContext<TResponse>,
) {
  switch (method) {
    case "DELETE":
      await evictDeletedResource(context);
      break;
    case "POST":
      await revalidateCreatedResource(context);
      break;
    case "PUT":
      await replaceUpdatedResource(context);
      break;
  }

  await revalidateInvalidatedCaches(method, context);
  await revalidateAffectedBatches(method, context);
}

async function revalidateAffectedBatches<TResponse>(
  method: ApiWriteMethod,
  context: ApiMutationCacheContext<TResponse>,
) {
  const invalidations =
    context.descriptor.invalidates?.[invalidationMethodByWriteMethod[method]] ??
    [];
  const matchesCollection = hasResourceCollectionCache(context)
    ? createResourceCollectionCacheMatcher(context)
    : undefined;
  function matchesEntry(key: unknown) {
    if (matchesExactApiGetRoute(key, context.requestedUrl)) return true;
    if (matchesInvalidatedResourceCache(key, invalidations, context.clientType))
      return true;
    if (matchesCollection == null) return false;
    return matchesCollection("get")(key) || matchesCollection("pagedGet")(key);
  }

  // A batch is one aggregate result (possibly with error wrappers). Re-fetch it
  // once if any member is affected; never populate it with a single PUT response.
  await context.swr.mutate((key) => matchesBatchApiGetCache(key, matchesEntry));
}
